import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminPayoutManagement.css';

const AdminPayoutManagement = () => {
  const [systemStats, setSystemStats] = useState({
    totalPayouts: 0,
    successfulPayouts: 0,
    failedPayouts: 0,
    pendingPayouts: 0,
    totalVolume: 0,
    successRate: 0
  });
  const [merchantPayouts, setMerchantPayouts] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('statistics');
  const [payoutSearch, setPayoutSearch] = useState('');

  useEffect(() => {
    fetchSystemStats();
    fetchMerchantPayouts();
  }, []);

  const fetchSystemStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/admin/payouts/stats', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data.success) {
        setSystemStats(response.data.data);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Payout stats: insufficient permissions');
      } else {
        console.error('Error fetching system stats:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchantPayouts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/admin/payouts/merchants', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data.success) {
        setMerchantPayouts(response.data.data);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Merchant payouts: insufficient permissions');
      } else {
        console.error('Error fetching merchant payouts:', error);
      }
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (loading) {
    return <div className="admin-payout-loading" role="status" aria-live="polite">Loading payout data...</div>;
  }

  return (
    <div className="admin-payout-management">
      <div className="admin-payout-header">
        <h2>💸 Payout Management</h2>
      </div>

      {/* View Tabs */}
      <div className="admin-payout-tabs">
        <button
          className={`tab-btn ${activeView === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveView('statistics')}
        >
          📊 Merchant Payout Breakdown
        </button>
        <button
          className={`tab-btn ${activeView === 'reconciliation' ? 'active' : ''}`}
          onClick={() => setActiveView('reconciliation')}
        >
          📋 Reconciliation
        </button>
      </div>

      {/* Statistics View */}
      {activeView === 'statistics' && (
        <div className="statistics-view">
          {/* System-Wide Stats
          <div className="stats-section">
            <h3>System-Wide Payout Statistics</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-content">
                  <div className="stat-label">Total Payouts</div>
                  <div className="stat-value">{systemStats.totalPayouts}</div>
                </div>
              </div>
              <div className="stat-card success">
                <div className="stat-icon">✅</div>
                <div className="stat-content">
                  <div className="stat-label">Successful</div>
                  <div className="stat-value">{systemStats.successfulPayouts}</div>
                </div>
              </div>
              <div className="stat-card failed">
                <div className="stat-icon">❌</div>
                <div className="stat-content">
                  <div className="stat-label">Failed</div>
                  <div className="stat-value">{systemStats.failedPayouts}</div>
                </div>
              </div>
              <div className="stat-card pending">
                <div className="stat-icon">⏳</div>
                <div className="stat-content">
                  <div className="stat-label">Pending</div>
                  <div className="stat-value">{systemStats.pendingPayouts}</div>
                </div>
              </div>
              <div className="stat-card volume">
                <div className="stat-icon">💵</div>
                <div className="stat-content">
                  <div className="stat-label">Total Volume</div>
                  <div className="stat-value">{formatAmount(systemStats.totalVolume)}</div>
                </div>
              </div>
              <div className="stat-card rate">
                <div className="stat-icon">📈</div>
                <div className="stat-content">
                  <div className="stat-label">Success Rate</div>
                  <div className="stat-value">{systemStats.successRate.toFixed(1)}%</div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Merchant Breakdown */}
          <div className="merchant-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0 }}>Merchant Payout Breakdown</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={payoutSearch}
                  onChange={e => setPayoutSearch(e.target.value)}
                  placeholder="Search by ID or name..."
                  style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', width: '200px' }}
                  aria-label="Search merchants"
                />
                {payoutSearch && (
                  <button onClick={() => setPayoutSearch('')} style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', cursor: 'pointer', background: '#f5f5f5' }}>Clear</button>
                )}
              </div>
            </div>
            <div className="merchant-table-wrapper">
              <table className="merchant-table" role="table">
                <caption className="sr-only">Merchant Payout Breakdown</caption>
                <thead>
                  <tr>
                    <th scope="col">Merchant ID</th>
                    <th scope="col">Username</th>
                    <th scope="col">Business Name</th>
                    <th scope="col">Total Payouts</th>
                    <th scope="col">Success Rate</th>
                    <th scope="col">Total Volume</th>
                    <th scope="col">Pending</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {merchantPayouts
                    .filter(m => {
                      if (!payoutSearch.trim()) return true;
                      const s = payoutSearch.trim().toLowerCase();
                      return String(m.merchantId).includes(s) || (m.businessName && m.businessName.toLowerCase().includes(s)) || (m.username && m.username.toLowerCase().includes(s));
                    })
                    .map((merchant) => (
                    <tr key={merchant.merchantId}>
                      <td>{merchant.merchantId}</td>
                      <td>{merchant.username || '—'}</td>
                      <td>{merchant.businessName || 'N/A'}</td>
                      <td>{merchant.totalPayouts}</td>
                      <td>
                        <span className={`rate-badge ${merchant.successRate >= 90 ? 'high' : merchant.successRate >= 70 ? 'medium' : 'low'}`}>
                          {merchant.successRate.toFixed(1)}%
                        </span>
                      </td>
                      <td>{formatAmount(merchant.totalVolume)}</td>
                      <td>{merchant.pendingPayouts}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => setSelectedMerchant(merchant)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}


      {/* Reconciliation View */}
      {activeView === 'reconciliation' && (
        <ReconciliationView />
      )}

      {/* Merchant Details Modal */}
      {selectedMerchant && (
        <MerchantDetailsModal
          merchant={selectedMerchant}
          onClose={() => setSelectedMerchant(null)}
        />
      )}
    </div>
  );
};

// Bank Providers View Component
const BankProvidersView = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await axios.get('/api/v1/admin/payouts/providers');

      if (response.data.success) {
        setProviders(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProvider = async (providerId, enabled) => {
    try {
      await axios.put(
        `/api/v1/admin/payouts/providers/${providerId}/toggle`,
        { enabled: !enabled }
      );

      fetchProviders();
    } catch (error) {
      console.error('Error toggling provider:', error);
      alert('Failed to update provider status');
    }
  };

  if (loading) {
    return <div className="view-loading" role="status" aria-live="polite">Loading bank providers...</div>;
  }

  return (
    <div className="providers-view">
      <div className="view-header">
        <h3>Bank Provider Configuration</h3>
        <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
          + Add Provider
        </button>
      </div>

      <div className="providers-grid">
        {providers.map((provider) => (
          <div key={provider.id} className="provider-card">
            <div className="provider-header">
              <h4>{provider.providerName}</h4>
              <span className={`provider-status ${provider.enabled ? 'enabled' : 'disabled'}`}>
                {provider.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="provider-details">
              <div className="detail-row">
                <span>API Base URL:</span>
                <span>{provider.apiBaseUrl}</span>
              </div>
              <div className="detail-row">
                <span>Timeout:</span>
                <span>{provider.timeoutSeconds}s</span>
              </div>
              <div className="detail-row">
                <span>Max Retries:</span>
                <span>{provider.maxRetries}</span>
              </div>
              <div className="detail-row">
                <span>Per Transaction Limit:</span>
                <span>₹{provider.perTransactionLimit?.toLocaleString() || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span>Daily Limit:</span>
                <span>₹{provider.dailyLimit?.toLocaleString() || 'N/A'}</span>
              </div>
            </div>
            <div className="provider-actions">
              <button
                className={`btn btn-sm ${provider.enabled ? 'btn-danger' : 'btn-success'}`}
                onClick={() => toggleProvider(provider.id, provider.enabled)}
              >
                {provider.enabled ? 'Disable' : 'Enable'}
              </button>
              <button className="btn btn-sm btn-secondary">
                Edit Config
              </button>
            </div>
          </div>
        ))}
      </div>

      {providers.length === 0 && (
        <div className="empty-state">
          <p>No bank providers configured yet.</p>
          <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
            Add Your First Provider
          </button>
        </div>
      )}
    </div>
  );
};

// Reconciliation View Component
const ReconciliationView = () => {
  const [reconciliationData, setReconciliationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchReconciliationData();
  }, [selectedDate]);

  const fetchReconciliationData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v1/admin/payouts/reconciliation?date=${selectedDate}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data.success) {
        setReconciliationData(response.data.data);
      }
    } catch (error) {
      // Don't let 401 bubble up to the global interceptor — handle it here
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Reconciliation: insufficient permissions or session expired');
      } else {
        console.error('Error fetching reconciliation data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="view-loading" role="status" aria-live="polite">Loading reconciliation data...</div>;
  }

  return (
    <div className="reconciliation-view">
      <div className="view-header">
        <h3>Reconciliation & Settlement</h3>
        <div className="date-selector">
          <label>Settlement Date:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="form-control"
          />
        </div>
      </div>

      <div className="reconciliation-summary">
        <div className="summary-card">
          <div className="summary-label">Total Records</div>
          <div className="summary-value">{reconciliationData.length}</div>
        </div>
        <div className="summary-card matched">
          <div className="summary-label">Matched</div>
          <div className="summary-value">
            {reconciliationData.filter(r => r.status === 'MATCHED').length}
          </div>
        </div>
        <div className="summary-card discrepancy">
          <div className="summary-label">Discrepancies</div>
          <div className="summary-value">
            {reconciliationData.filter(r => r.status !== 'MATCHED').length}
          </div>
        </div>
      </div>

      <div className="reconciliation-table-wrapper">
        <table className="reconciliation-table" role="table">
          <caption className="sr-only">Reconciliation Records</caption>
          <thead>
            <tr>
              <th scope="col">Payout ID</th>
              <th scope="col">Bank Reference</th>
              <th scope="col">Internal Amount</th>
              <th scope="col">Bank Amount</th>
              <th scope="col">Status</th>
              <th scope="col">Discrepancy</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reconciliationData.map((record) => (
              <tr key={record.id}>
                <td>{record.payoutId}</td>
                <td>{record.bankReference}</td>
                <td>₹{record.internalAmount?.toLocaleString()}</td>
                <td>₹{record.bankAmount?.toLocaleString()}</td>
                <td>
                  <span className={`status-badge ${record.status === 'MATCHED' ? 'matched' : 'discrepancy'}`}>
                    {record.status}
                  </span>
                </td>
                <td>{record.discrepancyReason || '-'}</td>
                <td>
                  {record.status !== 'MATCHED' && (
                    <button className="btn btn-sm btn-warning">
                      Resolve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reconciliationData.length === 0 && (
        <div className="empty-state">
          <p>No reconciliation data found for {selectedDate}</p>
        </div>
      )}
    </div>
  );
};

// Audit Logs View Component
const AuditLogsView = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    operation: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    fetchAuditLogs();
  }, [filters]);

  const fetchAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.operation) params.append('operation', filters.operation);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const response = await axios.get(`/api/v1/admin/payouts/audit?${params.toString()}`);

      if (response.data.success) {
        setAuditLogs(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="view-loading" role="status" aria-live="polite">Loading audit logs...</div>;
  }

  return (
    <div className="audit-view">
      <div className="view-header">
        <h3>Payout Audit Logs</h3>
      </div>

      <div className="audit-filters">
        <select
          value={filters.operation}
          onChange={(e) => setFilters({ ...filters, operation: e.target.value })}
          className="form-control"
        >
          <option value="">All Operations</option>
          <option value="CREATE">Create</option>
          <option value="APPROVE">Approve</option>
          <option value="REJECT">Reject</option>
          <option value="PROCESS">Process</option>
          <option value="STATUS_UPDATE">Status Update</option>
        </select>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          className="form-control"
          placeholder="From Date"
        />
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
          className="form-control"
          placeholder="To Date"
        />
      </div>

      <div className="audit-table-wrapper">
        <table className="audit-table" role="table">
          <caption className="sr-only">Payout Audit Logs</caption>
          <thead>
            <tr>
              <th scope="col">Timestamp</th>
              <th scope="col">Operation</th>
              <th scope="col">Payout ID</th>
              <th scope="col">User</th>
              <th scope="col">Details</th>
            </tr>
          </thead>
          <tbody>
            {auditLogs.map((log) => (
              <tr key={log.id}>
                <td>{new Date(log.timestamp).toLocaleString()}</td>
                <td>
                  <span className="operation-badge">{log.operation}</span>
                </td>
                <td>{log.payoutId}</td>
                <td>{log.userId}</td>
                <td className="details-cell">{log.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {auditLogs.length === 0 && (
        <div className="empty-state">
          <p>No audit logs found matching the current filters.</p>
        </div>
      )}
    </div>
  );
};

// Merchant Details Modal Component
const MerchantDetailsModal = ({ merchant, onClose }) => {
  const [payouts, setPayouts] = React.useState([]);
  const [loadingPayouts, setLoadingPayouts] = React.useState(true);
  const [modalFilters, setModalFilters] = React.useState({ status: '', search: '', minAmount: '', maxAmount: '' });

  React.useEffect(() => {
    const fetchPayouts = async () => {
      try {
        const res = await axios.get(`/api/v1/admin/payouts/merchants/${merchant.merchantId}/payouts`);
        if (res.data.success) setPayouts(res.data.data || []);
      } catch (e) {
        console.error('Failed to fetch merchant payouts:', e);
      } finally {
        setLoadingPayouts(false);
      }
    };
    fetchPayouts();
  }, [merchant.merchantId]);

  const formatAmount = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

  const filteredPayouts = payouts.filter(p => {
    if (modalFilters.status && p.status !== modalFilters.status) return false;
    if (modalFilters.search) {
      const s = modalFilters.search.toLowerCase();
      const match = (p.referenceId && p.referenceId.toLowerCase().includes(s)) ||
        (p.beneficiaryName && p.beneficiaryName.toLowerCase().includes(s)) ||
        (p.beneficiaryVpa && p.beneficiaryVpa.toLowerCase().includes(s));
      if (!match) return false;
    }
    if (modalFilters.minAmount && parseFloat(p.amount) < parseFloat(modalFilters.minAmount)) return false;
    if (modalFilters.maxAmount && parseFloat(p.amount) > parseFloat(modalFilters.maxAmount)) return false;
    return true;
  });

  const hasFilters = modalFilters.status || modalFilters.search || modalFilters.minAmount || modalFilters.maxAmount;

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal-content merchant-modal" role="dialog" aria-modal="true" aria-labelledby="merchant-details-title" style={{ maxWidth: '800px' }}>
        <div className="modal-header">
          <h3 id="merchant-details-title">Merchant Payout Details</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close merchant payout details">&times;</button>
        </div>
        <div className="modal-body">
          <div className="merchant-info">
            <h4>{merchant.businessName || 'N/A'}</h4>
            <p>Merchant ID: {merchant.merchantId}</p>
          </div>
          <div className="merchant-stats-grid">
            <div className="stat-item"><span className="stat-label">Total Payouts:</span><span className="stat-value">{merchant.totalPayouts}</span></div>
            <div className="stat-item"><span className="stat-label">Successful:</span><span className="stat-value success">{merchant.successfulPayouts}</span></div>
            <div className="stat-item"><span className="stat-label">Failed:</span><span className="stat-value failed">{merchant.failedPayouts}</span></div>
            <div className="stat-item"><span className="stat-label">Pending:</span><span className="stat-value pending">{merchant.pendingPayouts}</span></div>
            <div className="stat-item"><span className="stat-label">Total Volume:</span><span className="stat-value">₹{merchant.totalVolume?.toLocaleString()}</span></div>
            <div className="stat-item"><span className="stat-label">Success Rate:</span><span className="stat-value">{merchant.successRate?.toFixed(1)}%</span></div>
          </div>

          {/* Recent Payouts with Filters */}
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ marginBottom: '10px' }}>Recent Payouts</h4>
            
            {/* Filters */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px', alignItems: 'center' }}>
              <select value={modalFilters.status} onChange={e => setModalFilters({ ...modalFilters, status: e.target.value })}
                style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}>
                <option value="">All Status</option>
                <option value="SUCCESS">Success</option>
                <option value="FAILED">Failed</option>
                <option value="PENDING_APPROVAL">Pending</option>
                <option value="PROCESSING">Processing</option>
                <option value="INITIATED">Initiated</option>
              </select>
              <input type="text" value={modalFilters.search} onChange={e => setModalFilters({ ...modalFilters, search: e.target.value })}
                placeholder="Reference / Beneficiary" style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', width: '180px' }} />
              <input type="number" value={modalFilters.minAmount} onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 0) setModalFilters({ ...modalFilters, minAmount: v }); }}
                placeholder="Min ₹" min="0" style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', width: '80px' }} />
              <input type="number" value={modalFilters.maxAmount} onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 0) setModalFilters({ ...modalFilters, maxAmount: v }); }}
                placeholder="Max ₹" min="0" style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', width: '80px' }} />
              {hasFilters && (
                <button onClick={() => setModalFilters({ status: '', search: '', minAmount: '', maxAmount: '' })}
                  style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', background: '#f5f5f5' }}>Clear</button>
              )}
            </div>

            {loadingPayouts ? (
              <p role="status">Loading payouts...</p>
            ) : filteredPayouts.length === 0 ? (
              <p style={{ color: '#767676' }}>No payouts found{hasFilters ? ' matching filters' : ''}.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }} role="table">
                  <caption className="sr-only">Recent Payouts</caption>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th scope="col" style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Reference ID</th>
                      <th scope="col" style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Beneficiary Name</th>
                      <th scope="col" style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Amount</th>
                      <th scope="col" style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Status</th>
                      <th scope="col" style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayouts.slice(0, 20).map(p => (
                      <tr key={p.payoutId || p.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px', fontSize: '11px', fontFamily: 'monospace' }}>{p.referenceId}</td>
                        <td style={{ padding: '8px' }}>{p.beneficiaryName || p.beneficiaryVpa || 'N/A'}</td>
                        <td style={{ padding: '8px' }}>{formatAmount(p.amount)}</td>
                        <td style={{ padding: '8px' }}>{p.status}</td>
                        <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredPayouts.length > 20 && <p style={{ textAlign: 'center', fontSize: '12px', color: '#666', marginTop: '8px' }}>Showing 20 of {filteredPayouts.length} results</p>}
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default AdminPayoutManagement;
